import { CloudUpload, Delete, PhotoCamera } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useNotification, useTranslation } from "@refinedev/core";
import _ from "lodash";
import { FC, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { getClients } from "services/client";
import { getContainers } from "services/container";
import { uploadReceiptImages, uploadReceiptPdfs, uploadReceiptPdfsOcr } from "services/receipt";
import { mapParsedReceiptToForm } from "../utils/mapParsedReceipt";
import { ScanReceiptsCamera } from "./ScanReceiptsCamera";
import { UploadJob, UploadProgressWidget } from "./UploadProgressWidget";

interface ReiceptFormProps {}
export const ReiceptsForm: FC<ReiceptFormProps> = () => {
  /**
   * TRASNLATION
   */
  const { translate } = useTranslation();
  /******************* */
  /**
   * FORM PARAMS FROM CONTEXT
   */
  const form = useFormContext();
  /******************* */

  /**
   * NOTIFICATION
   */
  const { open } = useNotification();
  /******************* */

  /**
   * FORM HOOK FIELD ARRAY
   */
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "receipts",
  });
  /******************* */

  /**
   * FILE UPLOAD STATE
   */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pdfOcrInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [isUploadingPdfOcr, setIsUploadingPdfOcr] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploadJobs, setUploadJobs] = useState<UploadJob[]>([]);
  // Any running batch locks every upload button: a second batch would replace
  // the progress list of the first while it is still running.
  const anyUploading = isUploading || isUploadingPdf || isUploadingPdfOcr;
  // State updates land a render late; the camera can hand over a capture in
  // between, so the lock that actually prevents two batches is this ref.
  const uploadRunningRef = useRef(false);
  /******************* */

  /**
   * DATA
   */
  const { data: dataClients, refetch: refetchClients } = useQuery({
    queryKey: "clients",
    queryFn: getClients(),
  });

  const { data: dataContainers, refetch: refetchContainers } = useQuery({
    queryKey: "containers",
    queryFn: getContainers(false),
  });

  const toList = (value: any) => {
    const rows = Array.isArray(value)
      ? value
      : Array.isArray(value?.data)
        ? value.data
        : [];
    return rows.map((item: any) =>
      item?.attributes ? { id: item.id, ...item.attributes } : item
    );
  };

  const clientsList = toList(dataClients);
  const containersList = toList(dataContainers);

  const sameId = (a: any, b: any) =>
    a != null && b != null && String(a) === String(b);

  const normalizePerson = (value: string) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const matchClientByName = (name: string, list: any[]) => {
    const normalized = normalizePerson(name);
    if (!normalized) return null;
    const withoutDigits = normalized.replace(/\d+$/, "").trim();
    return (
      list.find((client: any) => {
        const clientName = normalizePerson(client.name);
        const clientBase = clientName.replace(/\d+$/, "").trim();
        return (
          clientName === normalized ||
          (withoutDigits.length >= 4 && clientBase === withoutDigits)
        );
      }) || null
    );
  };

  const mapBackendReceiptToForm = (
    item: any,
    lists?: { clients?: any[]; containers?: any[] }
  ) => {
    const mapped = mapParsedReceiptToForm(item);
    if (!mapped) {
      return null;
    }

    const containersForMatch = lists?.containers ?? containersList;
    const clientsForMatch = lists?.clients ?? clientsList;

    let clientId = mapped.client_id;
    if (clientId == null && mapped.client_name) {
      clientId = matchClientByName(mapped.client_name, clientsForMatch)?.id ?? null;
    }

    const mappedContainers = (item.containers || []).map((container: any) => {
      const containerName = (
        container.container_name ||
        container.name ||
        ""
      )
        .toString()
        .trim();
      const normalizedContainerName = containerName.toLowerCase();
      const backendId = container.container_id || container.id;

      let matchedContainer = null;
      if (backendId) {
        matchedContainer = containersForMatch.find((c: any) =>
          sameId(c.id, backendId)
        );
      }
      if (!matchedContainer && containerName) {
        const code = (containerName.match(/^(\d{1,3})/) || [])[1];
        matchedContainer = containersForMatch.find((c: any) => {
          const cName = c.name?.toString().trim() || "";
          const normalizedCName = cName.toLowerCase();
          if (
            normalizedCName === normalizedContainerName ||
            normalizedCName.startsWith(normalizedContainerName + " ")
          ) {
            return true;
          }
          return Boolean(code && new RegExp(`^${code}(?!\\d)`).test(cName));
        });
      }

      // Never invent a quantity. This used to fall back to 1 whenever the
      // parsed value looked odd, which wrote "1 crate" over a real count and
      // gave the reviewer nothing to notice. An unusable value now comes
      // through as 0 on a row the backend has already flagged for review.
      const quantity = Number(container.quantity);
      const qty = Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;

      if (matchedContainer) {
        return {
          id: matchedContainer.id,
          name: matchedContainer.name || containerName,
          quantity: qty,
        };
      }

      if (backendId) {
        return {
          id: backendId,
          name: containerName || String(backendId),
          quantity: qty,
        };
      }

      return {
        id: `pending-${containerName}`,
        name: containerName,
        quantity: qty,
        unmatched: true,
      };
    }).filter(Boolean);

    return {
      ...mapped,
      client_id: clientId,
      containers: mappedContainers,
    };
  };

  const applyMappedReceipts = (parsedReceipts: any[]) => {
    const currentRows = form.getValues("receipts") || [];
    // Drop any row the user has not touched, not just the single blank one the
    // page starts with: uploading twice, or landing here with receipts_number
    // set, used to leave empty rows wedged between the scanned ones.
    const isBlank = (row: any) =>
      !row?.receipt_number &&
      !row?.amount_received &&
      !row?.client_id &&
      !(row?.containers || []).length;
    const kept = currentRows.filter((row: any) => !isBlank(row));
    // One replace() rather than a replace/append branch plus append-in-a-loop.
    // Each append re-renders, and the mixed paths were why a stray blank row
    // survived an upload.
    replace([...kept, ...parsedReceipts]);
    console.log(
      `[receipt-scan] applied ${parsedReceipts.length} row(s), kept ${kept.length} existing:`,
      parsedReceipts.map((row: any) => ({
        receipt_number: row?.receipt_number,
        client_id: row?.client_id,
        client_name: row?.client_name,
        amount_received: row?.amount_received,
        containers: (row?.containers || []).map(
          (container: any) => `${container?.id}:${container?.name} x${container?.quantity}`
        ),
      }))
    );
  };

  const updateJob = (id: string, patch: Partial<UploadJob>) =>
    setUploadJobs((jobs) =>
      jobs.map((job) => (job.id === id ? { ...job, ...patch } : job))
    );

  const errorMessage = (error: any, fallback: string) =>
    error?.response?.data?.error?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

  /**
   * Shared upload pipeline.
   *
   * The image, PDF and PDF-OCR buttons differ only in which endpoint they call
   * and how they describe themselves; they were three ~100-line copies of the
   * same flow, so a fix to one (such as refetching containers before mapping)
   * had to be remembered three times.
   *
   * Files go to the server one per request, one at a time. A single request
   * for the whole batch gave no progress, and one slow photo (a scan can take
   * over a minute) could time the whole batch out and lose every result. Not
   * parallel on purpose: the scan creates missing clients and containers, and
   * two photos naming the same new one at once would create it twice.
   */
  const runUpload = async ({
    files,
    label,
    accept,
    upload,
    setBusy,
    inputRef,
    isUsableRow,
  }: {
    files: FileList | File[] | null;
    label: string;
    accept: (file: File) => boolean;
    upload: (files: File[]) => Promise<any>;
    setBusy: (busy: boolean) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    isUsableRow: (row: any) => boolean;
  }) => {
    if (!files || (files as ArrayLike<File>).length === 0) return;
    const fileArray = Array.from(files as ArrayLike<File>);
    // Reset now: the FileList is already copied, and a cleared input lets the
    // same files be picked again after a failure.
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    if (fileArray.some((file) => !accept(file))) {
      open?.({
        type: "error",
        message: "Error",
        description: `Please select only ${label} files`,
      });
      return;
    }

    if (uploadRunningRef.current) {
      open?.({
        type: "error",
        message: "Import already running",
        description: "Wait for the current import to finish, then try again",
      });
      return;
    }
    uploadRunningRef.current = true;

    const batch = Date.now();
    const jobs: UploadJob[] = fileArray.map((file, index) => ({
      id: `${batch}-${index}`,
      name: file.name || `${label} ${index + 1}`,
      status: "queued",
      receipts: 0,
      flagged: 0,
      skippedZero: 0,
    }));
    setUploadJobs(jobs);
    setBusy(true);

    let totalReceipts = 0;
    let totalFlagged = 0;
    let totalSkipped = 0;
    let failedFiles = 0;

    try {
      for (let index = 0; index < fileArray.length; index++) {
        const file = fileArray[index];
        const jobId = jobs[index].id;
        updateJob(jobId, { status: "processing" });
        try {
          const response = await upload([file]);
          const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : [];
          // "skipped" rows are receipts deliberately left out (received amount
          // 0), reported so the count adds up; they are not failures.
          const skippedZero = rows
            .filter((row: any) => row?.skipped)
            .reduce((sum: number, row: any) => sum + (Number(row.skipped_zero) || 0), 0);
          const failed = rows.filter((row: any) => !row?.success && !row?.skipped);
          const usable = rows.filter((row: any) => row?.success && isUsableRow(row));

          let parsedReceipts: any[] = [];
          if (usable.length > 0) {
            // Refetch first: the scan may have created a container or a
            // client, and mapping against a stale list shows them as unmatched.
            const [clientsResult, containersResult] = await Promise.all([
              refetchClients(),
              refetchContainers(),
            ]);
            parsedReceipts = usable
              .map((row: any) =>
                mapBackendReceiptToForm(row, {
                  clients: toList(clientsResult.data),
                  containers: toList(containersResult.data),
                })
              )
              .filter(Boolean);
          }

          if (parsedReceipts.length > 0) {
            // Applied as each file finishes, so the rows appear while the rest
            // of the batch is still being read.
            applyMappedReceipts(parsedReceipts);
          }

          const flagged = parsedReceipts.filter((row: any) => row?.needs_review).length;
          totalReceipts += parsedReceipts.length;
          totalFlagged += flagged;
          totalSkipped += skippedZero;

          if (parsedReceipts.length === 0 && skippedZero === 0) {
            failedFiles++;
            updateJob(jobId, {
              status: "error",
              error: failed[0]?.error || "No receipts found in this file",
            });
          } else {
            updateJob(jobId, {
              status: "done",
              receipts: parsedReceipts.length,
              flagged,
              skippedZero,
            });
          }
        } catch (error: any) {
          // One bad file must not abort the rest of the batch.
          console.error(`${label} upload failed for ${file.name}`, error?.response?.data ?? error);
          failedFiles++;
          updateJob(jobId, {
            status: "error",
            error: String(errorMessage(error, `Failed to upload ${file.name}`)),
          });
        }
      }
    } finally {
      uploadRunningRef.current = false;
      setBusy(false);
    }

    const skippedNote = totalSkipped ? `, ${totalSkipped} with amount 0 ignored` : "";
    if (totalReceipts === 0) {
      open?.({
        type: "error",
        message: totalSkipped ? "Nothing to import" : "Error",
        description: totalSkipped
          ? `All ${totalSkipped} receipt(s) had a received amount of 0 and were ignored`
          : `Failed to parse any receipts from the uploaded ${label} file(s)`,
      });
      return;
    }
    const attention = totalFlagged > 0 || failedFiles > 0;
    open?.({
      type: attention ? "progress" : "success",
      message: attention ? "Check the highlighted rows" : "Success",
      description:
        `Parsed ${totalReceipts} receipt(s) from ${fileArray.length - failedFiles}/${fileArray.length} ${label} file(s)` +
        (totalFlagged ? `; ${totalFlagged} need checking before saving` : "") +
        skippedNote,
    });
  };

  const isPdf = (file: File) =>
    file.type === "application/pdf" || Boolean(file.name?.endsWith(".pdf"));

  const handleFileUpload = (files: FileList | File[] | null) =>
    runUpload({
      files,
      label: "image",
      accept: (file) => file.type.startsWith("image/"),
      upload: uploadReceiptImages,
      setBusy: setIsUploading,
      inputRef: fileInputRef,
      isUsableRow: (row) =>
        Boolean(row.receipt_no || row.number || row.needs_review || row.client_id),
    });

  const handlePdfUpload = (files: FileList | null) =>
    runUpload({
      files,
      label: "PDF",
      accept: isPdf,
      upload: uploadReceiptPdfs,
      setBusy: setIsUploadingPdf,
      inputRef: pdfInputRef,
      isUsableRow: () => true,
    });

  const handlePdfOcrUpload = (files: FileList | null) =>
    runUpload({
      files,
      label: "PDF (OCR)",
      accept: isPdf,
      upload: uploadReceiptPdfsOcr,
      setBusy: setIsUploadingPdfOcr,
      inputRef: pdfOcrInputRef,
      isUsableRow: (row) => Array.isArray(row.containers),
    });

  return (
    <>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {/* <GoogleSheetParser
            parsedData={(parsedData) => {
              console.log({ parsedData });
              remove();
              append(parsedData.filter((_, idx) => idx > 1));
            }}
          /> */}
        
        {/* File Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            <Button
              variant="outlined"
              startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
              disabled={anyUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading ? "Processing..." : "Upload Images"}
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={isUploading ? <CircularProgress size={16} /> : <PhotoCamera />}
              disabled={anyUploading}
              onClick={() => setCameraOpen(true)}
            >
              Scan with camera
            </Button>
            
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handlePdfUpload(e.target.files)}
            />
            <Button
              variant="outlined"
              color="secondary"
              startIcon={isUploadingPdf ? <CircularProgress size={16} /> : <CloudUpload />}
              disabled={anyUploading}
              onClick={() => pdfInputRef.current?.click()}
            >
              {isUploadingPdf ? "Processing..." : "Upload PDF"}
            </Button>
            <input
              ref={pdfOcrInputRef}
              type="file"
              accept="application/pdf"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handlePdfOcrUpload(e.target.files)}
            />
            <Button
              variant="outlined"
              color="info"
              startIcon={isUploadingPdfOcr ? <CircularProgress size={16} /> : <CloudUpload />}
              disabled={anyUploading}
              onClick={() => pdfOcrInputRef.current?.click()}
            >
              {isUploadingPdfOcr ? "Processing..." : "Upload PDF (OCR)"}
            </Button>
          </Stack>
          <Typography variant="caption" display="block" color="text.secondary">
            Scan highlights each full receipt ticket (not product lines). Unclear
            receipt numbers stay empty so you can re-verify. Amount given stays 0.
          </Typography>
        </Box>

        <Controller
          name={`day`}
          control={form.control}
          defaultValue={new Date()}
          render={({ field }) => (
            <DatePicker
              {...field}
              value={new Date(field?.value)}
              label="Day"
              format="yyyy-MM-dd"
            />
          )}
        />

        <Stack
          direction="column"
          alignItems="start"
          spacing={4}
          mt={4}
          width="100%"
          sx={{
            p: 2,
          }}
        >
          {(fields || []).map((item: any, index: any) => {
            const receiptNumber = `receipts.${index}.receipt_number` as any;
            const clientId = `receipts.${index}.client_id` as any;
            const amountReceived = `receipts.${index}.amount_received` as any;
            const amountGiven = `receipts.${index}.amount_given` as any;
            const containers = `receipts.${index}.containers` as any;

            const currentContainers = form.watch(containers) || [];

            const needsReview = Boolean(form.watch(`receipts.${index}.needs_review` as any));
            const reviewReasons: string[] =
              form.watch(`receipts.${index}.review_reasons` as any) || [];

            const scannedReceiptNumber = form.watch(receiptNumber);

            return (
              <Stack
                key={item.id}
                direction="column"
                spacing={1}
                sx={{
                  p: 2.5,
                  borderRadius: 1,
                  outline: needsReview ? "2px solid #ed6c02" : undefined,
                  backgroundColor: needsReview ? "#fff8e1" : undefined,
                }}
              >
                {needsReview && (
                  // Padding, not margin: the parent Stack resets child margins.
                  // It offsets the Client field's mt: -23px below, which
                  // otherwise overlaps the last reason.
                  <Stack spacing={0.25} sx={{ pb: "32px" }}>
                    <Typography variant="caption" color="warning.dark" fontWeight="bold">
                      {scannedReceiptNumber
                        ? "Check this row before saving"
                        : "Receipt number unclear — re-verify before saving"}
                    </Typography>
                    {/* The backend says exactly what it could not verify — a
                        failed checksum reads very differently from an
                        unmatched container, and the reviewer needs to know
                        which one to look at. */}
                    {reviewReasons.map((reason: string) => (
                      <Typography key={reason} variant="caption" color="warning.dark">
                        • {reason}
                      </Typography>
                    ))}
                  </Stack>
                )}
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="start"
                >
                  <Controller
                    name={clientId}
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <FormControl
                          sx={{ width: 250, mt: "-23px !important" }}
                          variant="outlined"
                          size="small"
                        >
                          <FormLabel>Client</FormLabel>
                          {/* Every option value is a string. MUI matches the
                              selected value with ===, so a numeric client_id
                              from the scan against a string option id (or the
                              reverse) renders an empty Select — and because
                              sameId() compares as strings, the "scanned
                              client" fallback was suppressed at the same time,
                              leaving no clue that a client had been matched. */}
                          <Select
                            {...field}
                            value={field.value != null ? String(field.value) : ""}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === "") return field.onChange(null);
                              const picked = clientsList.find((c: any) =>
                                sameId(c.id, raw)
                              );
                              field.onChange(picked ? picked.id : raw);
                            }}
                            name="client"
                            label="Client"
                            displayEmpty
                          >
                            <MenuItem value="">Select Client</MenuItem>
                            {field.value != null &&
                              !clientsList.some((c: any) =>
                                sameId(c.id, field.value)
                              ) && (
                                <MenuItem value={String(field.value)}>
                                  {form.watch(`receipts.${index}.client_name` as any) ||
                                    "Scanned client"}
                                </MenuItem>
                              )}
                            {clientsList.map(
                              (client: any) => (
                                <MenuItem key={client.id} value={String(client.id)}>
                                  {client?.name}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {!field.value &&
                            form.watch(`receipts.${index}.client_name` as any) && (
                              <Typography variant="caption" color="text.secondary">
                                Scanned: {form.watch(`receipts.${index}.client_name` as any)}
                              </Typography>
                            )}
                        </FormControl>
                      );
                    }}
                  />

                  <Autocomplete
                    multiple
                    onChange={(e, value: any) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Merge current containers with newly selected ones, default quantity = 1 and keep unique by id
                      const merged = _.uniqBy(
                        [
                          ...currentContainers,
                          ...value.map((v: any) => ({ ...v, quantity: 1 })),
                        ],
                        "id"
                      );
                      form.setValue(containers, merged);
                    }}
                    size="small"
                    value={[] as any[]}
                    inputValue=""
                    disablePortal
                    id="combo-box-demo"
                    options={[
                      ...containersList,
                      ...currentContainers.filter(
                        (container: any) =>
                          container?.id &&
                          !containersList.some((option: any) =>
                            String(option.id) === String(container.id)
                          )
                      ),
                    ]}
                    sx={{
                      maxHeight: 40,
                      width: 250,
                    }}
                    getOptionLabel={(option: any) => option?.name}
                    renderInput={(params) => (
                      <TextField {...params} label="Containers" />
                    )}
                  />

                  <Controller
                    name={receiptNumber}
                    control={form.control}
                    rules={{
                      required: translate("this-field-is-required"),
                      validate: (value) => {
                        if (!value) return true;
                        const rows = form.getValues("receipts") || [];
                        const isUnique = rows.every(
                          (row: any, idx: number) =>
                            idx === index ||
                            String(row?.receipt_number || "") !== String(value)
                        );
                        return (
                          isUnique || translate("receipt-number-must-be-unique")
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        variant="outlined"
                        size="small"
                        type="number"
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        }}
                        label="Receipt Number"
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={
                          needsReview && !field.value
                            ? "Unclear from scan — type the last 3 digits"
                            : fieldState.error?.message
                        }
                      />
                    )}
                  />

                  {/* Controlled, like Receipt Number. register() leaves the
                      input uncontrolled, so a value written into the field
                      array after mount is not pushed back into the DOM. */}
                  <Controller
                    name={amountReceived}
                    control={form.control}
                    rules={{ required: translate("this-field-is-required") }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value === "" ? null : event.target.value
                          )
                        }
                        variant="outlined"
                        size="small"
                        label="Amount Received"
                        InputLabelProps={{ shrink: true }}
                        type="number"
                        error={!!fieldState.error}
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        }}
                      />
                    )}
                  />
                  <Box>
                    <Controller
                      name={amountGiven}
                      control={form.control}
                      rules={{ required: translate("this-field-is-required") }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          onChange={(event) =>
                            field.onChange(
                              event.target.value === "" ? null : event.target.value
                            )
                          }
                          variant="outlined"
                          size="small"
                          label="Amount Given"
                          InputLabelProps={{ shrink: true }}
                          type="number"
                          error={!!fieldState.error}
                          sx={{
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                              {
                                display: "none",
                              },
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                          }}
                        />
                      )}
                    />
                    {Number(form.watch(amountReceived)) -
                      Number(form.watch(amountGiven)) >
                      0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        Difference:{" "}
                        {Number(form.watch(amountReceived)) -
                          Number(form.watch(amountGiven))}
                      </Typography>
                    )}
                  </Box>

                  <IconButton onClick={() => remove(index)}>
                    <Delete />
                  </IconButton>
                </Stack>
                {currentContainers?.length > 0 ? (
                  <Box>
                    <Typography fontWeight="bold">Containers</Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      width="100%"
                      sx={{
                        overflowX: "auto",
                        p: 2,
                        backgroundColor: "#e7e5e557",
                        borderRadius: 1,
                      }}
                    >
                      {(currentContainers || [])?.map(
                        (item: any, index: number) => {
                          return (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Chip
                                color={item?.unmatched ? "warning" : "default"}
                                label={`${item?.name} x${item?.quantity ?? 1}${
                                  item?.unmatched ? " — pick a container" : ""
                                }`}
                                onDelete={() =>
                                  form.setValue(
                                    containers,
                                    currentContainers.filter(
                                      (filtredItem: any) =>
                                        filtredItem.id !== item.id
                                    )
                                  )
                                }
                              />
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantity ?? 1}
                                onChange={(e) => {
                                  const newQuantity = Number(e.target.value);
                                  if (newQuantity >= 0) {
                                    const updated = currentContainers.map(
                                      (c: any) =>
                                        c.id === item.id
                                          ? { ...c, quantity: newQuantity }
                                          : c
                                    );
                                    form.setValue(containers, updated);
                                  }
                                }}
                                sx={{ width: 70 }}
                              />
                            </Stack>
                          );
                        }
                      )}
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      </Box>
      <Button
        onClick={() =>
          append({
            receipt_number: null,
            amount_received: null,
            amount_given: 0,
            client_id: null,
            containers: [],
            needs_review: false,
            review_reasons: [],
          })
        }
      >
        Add data
      </Button>
      <ScanReceiptsCamera
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(files) => handleFileUpload(files)}
      />
      <UploadProgressWidget jobs={uploadJobs} onClose={() => setUploadJobs([])} />
    </>
  );
};

export const SummaryTotals: FC<{
  label: string;
  total: number;
  color: string;
  textColor: string;
}> = ({ label, total, color, textColor }): JSX.Element => {
  return (
    <Box
      sx={{
        width: "100%",
        height: 92,
        p: 2,
        backgroundColor: color,
        borderRadius: 2,
        opacity: 0.9,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: textColor,
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "24px", fontWeight: "bold" }}>
        {total}
      </Typography>
    </Box>
  );
};
